import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const LisburnChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Management Lisburn | Back Pain Hypnotherapy</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Lisburn from Belfast clinic (8 miles). Neuroscience-based approach for office worker pain, back pain, neck pain, fibromyalgia. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Lisburn, back pain hypnotherapy Lisburn, fibromyalgia Lisburn, neck pain treatment Lisburn, desk work pain" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lisburn-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Management Lisburn | Back Pain Hypnotherapy" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Lisburn. Neuroscience-based approach for office worker pain, back pain, neck pain." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Management Lisburn",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Lisburn from Belfast clinic. Specializing in office worker pain, back pain, neck pain, fibromyalgia, and work-related musculoskeletal pain with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/lisburn-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Management",
            "areaServed": {
              "@type": "City",
              "name": "Lisburn",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Wellness Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with office worker back pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for office worker pain including back pain, neck pain, and RSI. Research shows chronic pain is amplified by stress, and commuter professionals experience significant stress. By calming your nervous system, reducing work-related stress, and teaching your brain healthier pain processing patterns, many Lisburn professionals experience significant pain reduction within 6-8 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "How convenient is the Belfast clinic for Lisburn commuters?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast. From Lisburn it is a straightforward run up the M1 into Belfast and then south along the Ormeau Road, typically around 20 minutes off-peak. For professionals who already commute toward Belfast for work, the clinic is conveniently located - many clients schedule sessions before or after work. Lisburn is also on the rail line into Belfast, with Botanic and City Hospital the closest stations to the clinic."
                }
              },
              {
                "@type": "Question",
                "name": "Can I fit pain management sessions around my work schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Clinic days and times vary, and early evening appointments are available - current availability and online booking are on the clinic's own page: https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37. Many Lisburn professionals fit sessions into their existing commute routine. If weekday timing is difficult, online sessions offer complete flexibility for evenings or weekends."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work alongside physiotherapy for pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hypnotherapy is complementary to physiotherapy and medical care, not a replacement. Many Lisburn clients use hypnotherapy alongside NHS physiotherapy, osteopathy, or medication. While physio addresses physical factors, hypnotherapy addresses the stress and nervous system components that amplify pain. The combination is often more effective than either approach alone."
                }
              },
              {
                "@type": "Question",
                "name": "What work-related pain conditions do you treat?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Lisburn professionals experiencing chronic back pain from desk work, neck and shoulder pain from computer use, RSI and carpal tunnel syndrome, tension headaches from screen time, fibromyalgia exacerbated by work stress, and commuter-related pain from driving. The neuroscience-based approach addresses both the pain itself and the work stress that amplifies it."
                }
              },
              {
                "@type": "Question",
                "name": "Can I claim pain management hypnotherapy on health insurance?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Some private health insurance policies cover hypnotherapy when provided by a CNHC registered hypnotherapist for recognized conditions such as chronic pain management. Coverage varies between providers. I recommend contacting your insurance provider to ask about coverage for CNHC registered hypnotherapy. I can provide receipts and documentation for insurance claims as needed."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Lisburn
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Lisburn from Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-4 mb-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">8 miles</span> from Lisburn via M1 • <span className="font-semibold">15-20 min drive</span>
                </p>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Train className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Train</span> to Botanic or City Hospital, the closest stations to the clinic
                </p>
              </div>
              <p className="text-gray-600 mt-3">Online sessions also available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Lisburn Professionals
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Lisburn from our professional Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast - the M1 into Belfast, then south along the Ormeau Road. The short drive makes accessing specialist chronic pain hypnotherapy convenient for Lisburn residents. For professionals who already commute toward Belfast for work, our clinic location is easily accessible - many clients fit pain management sessions into their existing journeys.
              </p>
              <p>
                Chronic pain is surprisingly common among Lisburn's professional working population. Years of desk work, long hours at computers, commuter driving, work-related stress, and poor workplace ergonomics create perfect conditions for back pain, neck pain, fibromyalgia, tension headaches, and repetitive strain injuries to develop and persist. Many professionals push through pain for years, taking painkillers and hoping it will resolve, until the pain becomes unbearable or starts affecting work performance.
              </p>
              <p>
                Modern neuroscience shows us that chronic pain isn't just about tissue damage - it's processed in the nervous system, and when we're stressed or anxious (as many commuter professionals are), pain signals intensify. This is where solution-focused hypnotherapy excels. By calming your fight-or-flight response, teaching your brain healthier pain processing patterns, and addressing the work stress and anxiety that amplify pain, many clients experience significant reductions in pain levels and improvements in daily functioning.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I understand chronic pain conditions from both a medical and therapeutic perspective. I've worked with countless pain patients and understand the frustration when you're told "it's just stress" or "you'll have to live with it." Hypnotherapy offers a science-backed approach that works alongside your medical care to help you regain control, reduce pain levels, and improve your quality of life at work and home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Office Worker Pain Focus */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Office Worker Pain: Why It Happens
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Lisburn's professional population faces unique pain challenges from desk work, commuting, and work-related stress
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Prolonged Sitting</h3>
                <p className="text-gray-700">
                  Hours at a desk compress spinal discs, weaken core muscles, and create postural imbalances. Add commuter driving time, and many professionals sit 10-12 hours daily, creating chronic back and neck pain.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Work-Related Stress</h3>
                <p className="text-gray-700">
                  Deadlines, demanding managers, job insecurity, and constant connectivity keep your stress response activated. Chronic stress amplifies pain signals and creates muscle tension, particularly in neck and shoulders.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Repetitive Strain</h3>
                <p className="text-gray-700">
                  Typing, mouse work, and screen time create repetitive strain injuries in wrists, forearms, and shoulders. Without proper breaks and ergonomics, these develop into chronic pain conditions like RSI and carpal tunnel syndrome.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                The stress-pain connection is crucial for Lisburn professionals to understand. When you're stressed, your nervous system amplifies pain signals - a tension headache becomes a migraine, mild back discomfort becomes debilitating pain. Your brain interprets stress as danger, keeping your pain alarm system on high alert.
              </p>
              <p className="text-gray-700 text-lg">
                Hypnotherapy addresses this stress-pain cycle directly. By calming your nervous system, reducing work-related stress, improving sleep quality, and teaching your brain healthier pain processing patterns, many professionals experience significant pain relief that physiotherapy or medication alone couldn't achieve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with your nervous system to reduce pain perception
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Calm the Stress Response</h3>
                    <p className="text-gray-700">
                      Reduces fight-or-flight activation that amplifies pain signals. For stressed professionals, this stress reduction often produces more pain relief than expected.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Activates descending pain inhibition pathways to reduce pain signal intensity traveling from your spine to your brain. This neurological "volume control" is powerful.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Rewire Pain Pathways</h3>
                    <p className="text-gray-700">
                      Uses neuroplasticity to create new neural pathways. Your brain has learned pain patterns through repetition - it can learn healthier patterns the same way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Improve Sleep Quality</h3>
                    <p className="text-gray-700">
                      Poor sleep intensifies pain perception. By improving sleep quality through relaxation techniques and stress reduction, pain levels often decrease significantly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                Research supports hypnotherapy's effectiveness for chronic pain. A major review found hypnotherapy 68% more effective than no treatment for chronic pain conditions. For busy Lisburn professionals, the time-efficient approach of 6-8 sessions producing lasting results is particularly appealing compared to lengthy traditional therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Work-Related Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain from prolonged sitting, poor posture, and commuter driving. Stress reduction often dramatically improves back pain intensity.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Neck & Shoulder Pain</h3>
                <p className="text-gray-700">Upper back, neck, and shoulder tension from computer work and stress. The body holds stress in these areas, creating chronic muscle tension and pain.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">RSI & Carpal Tunnel</h3>
                <p className="text-gray-700">Repetitive strain injuries from keyboard and mouse use. Hypnotherapy reduces pain perception and helps break the pain-fear-tension cycle.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Tension Headaches & Migraines</h3>
                <p className="text-gray-700">Chronic headaches from screen time and work stress. Stress reduction and trigger management can significantly reduce frequency and intensity.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain and fatigue exacerbated by work stress. Hypnotherapy addresses the nervous system sensitization underlying fibromyalgia symptoms.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Commuter-Related Pain</h3>
                <p className="text-gray-700">Back, hip, and leg pain from daily driving between Lisburn and Belfast. Reducing driving-related stress often reduces associated pain.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NHS Complementary */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system and stress components of pain, hypnotherapy enhances these treatments and helps you regain function and quality of life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your GP, physiotherapist, or pain specialist. Our proximity to Belfast City Hospital (just 5 minutes away) makes hypnotherapy particularly convenient for Lisburn residents receiving medical treatment who need complementary pain management support.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                I work collaboratively with your healthcare team, not in competition with them. Many Lisburn professionals use hypnotherapy alongside NHS physiotherapy, private osteopathy, or workplace wellness programs for a comprehensive approach to pain management that addresses both physical and psychological factors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Lisburn Professionals Choose Sharon Clare
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Convenient for Commuters
                </h3>
                <p className="text-gray-700">
                  Just 8 miles from Lisburn via M1 (15-20 minutes). If you already commute to Belfast for work, sessions fit seamlessly into your routine. Many clients schedule appointments on their commute days, eliminating additional travel.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  15 Years Nursing Experience
                </h3>
                <p className="text-gray-700">
                  Medical background brings clinical credibility to pain management. I understand pain conditions, medications, and medical terminology from working at Belfast City Hospital for 15 years.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Availability
                </h3>
                <p className="text-gray-700">
                  Belfast clinic days and times vary, and early evening appointments are available - so sessions can usually be fitted around professional work schedules.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Approach
                </h3>
                <p className="text-gray-700">
                  Neuroscience-backed techniques supported by research, not pseudoscience. Results-focused approach that respects professional clients' time and logical thinking.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Lisburn
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                    <p className="text-gray-700 mb-2">M1 north from Lisburn into Belfast (around 20 minutes off-peak)</p>
                    <p className="text-gray-700">Then south along the Ormeau Road (A24) to number 228</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Train className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Train</h3>
                    <p className="text-gray-700 mb-2">Lisburn to Botanic or City Hospital, the closest stations to the clinic</p>
                    <p className="text-gray-700">Short taxi from the station to the Ormeau Road</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Lisburn Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Years of desk work at my Lisburn office had left me with chronic back pain. I was taking painkillers daily and still struggling. Sharon's hypnotherapy approach addressed the stress component I hadn't realized was amplifying my pain. After 8 sessions, my pain levels are down 70% and I've cut my painkiller use in half."
                </p>
                <p className="font-semibold text-gray-900">J.T., Lisburn - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The drive from Lisburn was so easy - I scheduled sessions on my way home from work in Belfast. Sharon helped me manage fibromyalgia pain that had controlled my life for 6 years. Her nursing background meant she really understood my condition. My pain hasn't disappeared but it's manageable now, and I'm functioning so much better."
                </p>
                <p className="font-semibold text-gray-900">S.M., Hillsborough - Fibromyalgia</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "RSI from years of computer work was affecting my ability to do my job. Physiotherapy helped but I was still in pain. Adding hypnotherapy made the real difference - addressing the stress and pain amplification. Within 6 sessions I could work full days again without constant wrist pain."
                </p>
                <p className="font-semibold text-gray-900">M.H., Moira - RSI Pain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lisburn Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with office worker back pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for office worker pain including chronic back pain, neck pain, and RSI. Research shows chronic pain is amplified by stress, and commuter professionals experience significant work-related stress. By calming your nervous system, reducing work stress, improving sleep quality, and teaching your brain healthier pain processing patterns, many Lisburn professionals experience significant pain reduction within 6-8 sessions. The approach works particularly well alongside physiotherapy or ergonomic workplace changes.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How convenient is the Belfast clinic for Lisburn commuters?
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast - the M1 into Belfast from Lisburn, then south along the Ormeau Road, typically around 20 minutes off-peak. For professionals who already commute toward Belfast for work, the clinic is conveniently located - many clients schedule sessions before or after work. Lisburn is also on the rail line into Belfast, and Botanic and City Hospital stations are the closest to the clinic. Online sessions are available if you prefer to avoid travel.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I fit pain management sessions around my work schedule?
                </h3>
                <p className="text-gray-700">
                  Yes. Clinic days and times vary, and early evening appointments are available - current availability and online booking are on the clinic's own page at <a href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">belfastchiropracticclinic.janeapp.co.uk</a>. Many Lisburn professionals schedule sessions on their regular commute day to eliminate additional travel. If those times don't work, online sessions offer complete flexibility for evenings or weekends from your home in Lisburn.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work alongside physiotherapy for pain?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Hypnotherapy is complementary to physiotherapy and medical care, not a replacement. Many Lisburn clients use hypnotherapy alongside NHS physiotherapy, private osteopathy, or workplace wellness programs. While physiotherapy addresses physical factors like muscle weakness and joint mobility, hypnotherapy addresses the stress and nervous system components that amplify pain. The combination is often more effective than either approach alone, producing faster, more lasting results.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What work-related pain conditions do you treat?
                </h3>
                <p className="text-gray-700">
                  I work with Lisburn professionals experiencing chronic back pain from desk work and commuter driving, neck and shoulder pain from computer use, RSI and carpal tunnel syndrome from keyboard work, tension headaches and migraines from screen time and stress, fibromyalgia exacerbated by work pressure, and general musculoskeletal pain from prolonged sitting. The neuroscience-based approach addresses both the pain itself and the work stress and anxiety that amplify pain perception.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I claim pain management hypnotherapy on health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance policies cover hypnotherapy when provided by a CNHC registered hypnotherapist for recognized conditions such as chronic pain management. Coverage varies significantly between providers and individual policies. I recommend contacting your insurance provider directly to ask about coverage for CNHC registered hypnotherapy for pain management. I can provide receipts and documentation for insurance claims as needed. Even without insurance coverage, many Lisburn clients find the investment worthwhile given the significant improvement in work performance and quality of life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with work-related chronic pain, book your initial consultation today and start addressing the stress-pain connection."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LisburnChronicPain;
